import React, { useState, useEffect } from 'react';
import './QuizDialog.css';

interface QuizDialogProps {
  question: string;
  options: string[];
  questionId: number;
  milestone: string;
  correctAnswer: string;
  onAnswer: (answer: string, isCorrect: boolean) => void;
  onSkip?: () => void;
  visible: boolean;
  isValidating?: boolean; // NEW: Show loading spinner during backend validation
  validationResult?: 'correct' | 'incorrect' | null; // NEW: Result from backend
  isCompleting?: boolean; // NEW: Quiz completion phrase detected - disable buttons
}

type DialogState = 'idle' | 'validating' | 'correct' | 'incorrect' | 'disappearing';

const QuizDialog: React.FC<QuizDialogProps> = ({
  question,
  options,
  questionId,
  milestone,
  correctAnswer,
  onAnswer,
  onSkip,
  visible,
  isValidating = false,
  validationResult = null,
  isCompleting = false
}) => {
  const [dialogState, setDialogState] = useState<DialogState>('idle');
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);

  // Reset state when new question appears
  useEffect(() => {
    if (visible) {
      setDialogState('idle');
      setSelectedAnswer(null);
    }
  }, [questionId, visible]);

  // Handle validation state changes from parent
  useEffect(() => {
    if (isValidating) {
      setDialogState('validating');
    } else if (validationResult === 'correct') {
      setDialogState('correct');
      setTimeout(() => {
        setDialogState('disappearing');
      }, 2000);
    } else if (validationResult === 'incorrect') {
      setDialogState('incorrect');
      setTimeout(() => {
        setDialogState('idle');
        setSelectedAnswer(null);
      }, 1500);
    }
  }, [isValidating, validationResult]);

  const handleAnswerClick = (answer: string) => {
    if (dialogState !== 'idle' || isCompleting) return; // Prevent clicks during validation or completion
    
    setSelectedAnswer(answer);
    const isCorrect = answer === correctAnswer;
    
    // Call parent handler to validate with backend
    // Parent will set isValidating=true, then update validationResult when done
    onAnswer(answer, isCorrect);
  };

  const handleSkipClick = () => {
    if (onSkip && dialogState === 'idle' && !isCompleting) {
      onSkip();
    }
  };

  if (!visible) return null;

  return (
    <div className="quiz-dialog-overlay">
      <div className={`quiz-dialog ${dialogState}`}>
        {/* Header with milestone badge */}
        <div className="quiz-dialog-header">
          <div className="quiz-critter-icon">🦎</div>
          <div className="milestone-badge">{milestone} Complete!</div>
        </div>

        {/* Question text */}
        <div className="quiz-question">
          <p>{question}</p>
        </div>

        {/* Multiple choice options */}
        <div className="quiz-options">
          {options.map((option, index) => (
            <button
              key={index}
              className={`quiz-option ${selectedAnswer === option ? 'selected' : ''}`}
              onClick={() => handleAnswerClick(option)}
              disabled={dialogState !== 'idle' || isCompleting}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>

        {/* Close button */}
        {onSkip && (
          <button 
            className="quiz-skip-button" 
            onClick={handleSkipClick}
            disabled={dialogState !== 'idle' || isCompleting}
          >
            Close
          </button>
        )}

        {/* Loading/Validation indicator */}
        {dialogState === 'validating' && (
          <div className="quiz-feedback validating">
            <span className="feedback-spinner">⏳</span>
            <span className="feedback-text">Checking answer...</span>
          </div>
        )}

        {/* Feedback indicator */}
        {dialogState === 'correct' && (
          <div className="quiz-feedback correct">
            <span className="feedback-icon">✓</span>
            <span className="feedback-text">Great job!</span>
          </div>
        )}
        {dialogState === 'incorrect' && (
          <div className="quiz-feedback incorrect">
            <span className="feedback-icon">✗</span>
            <span className="feedback-text">Try again!</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizDialog;
